import { Text } from 'src/onboarding/components/typography/Text/Text';
import styles from './style.module.scss';
import { t } from '@web-solutions/module-localization';
import { Caption } from 'src/onboarding/components/typography/Caption/Caption';

import { useStore } from 'src/onboarding/store/store';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import yogurt from '../../../../assets/images/yogurtMeal.webp';
import beef from '../../../../assets/images/beefMeal.webp';
import toast from '../../../../assets/images/toastMeal.webp';
import tortilla from '../../../../assets/images/tortillaMeal.webp';
import { mealMappings } from './mealMappings';
import pancakes from '../../../../assets/images/pancakesMeal.webp';
import tako from '../../../../assets/images/tako.webp';
import humus from '../../../../assets/images/humus.webp';
import dinner64 from '../../../../assets/images/dinner64.webp';
import { useRemoteConfig } from '@web-solutions/base-app/hooks/use-remote-config';
import getLocale from 'src/localization/get-locale';

const MealPlanContainer = () => {
  const weight = useStore((state) => state.weight);
  const idealWeight = useStore((state) => state.idealWeight);
  const breakfast = useStore((state) => state.breakfast);
  const snacks = useStore((state) => state.snacks);
  const chooseBreakfast = useStore((state) => state.chooseBreakfast);
  const chooseLunch = useStore((state) => state.chooseLunch);
  const chooseDinner = useStore((state) => state.chooseDinner);
  const chooseSnack = useStore((state) => state.chooseSnack);

  const { addBlurDay1, personalPlanPage } = useRemoteConfig();
  const locale = getLocale();

  const hasWeightData = () => {
    const hasCurrentWeight = weight && (weight.lbs || weight.kg);
    const hasIdealWeight = idealWeight && (idealWeight.value || idealWeight.kg);
    return hasCurrentWeight && hasIdealWeight;
  };

  const getWeightDifferenceText = (
    currentWeight: { type: string; lbs?: string; kg?: string },
    idealWeight: { type: string; value?: string; kg?: string }
  ) => {
    try {
      if (!hasWeightData()) {
        return t('onboarding.mealPlanContainer.defaultTitle');
      }

      const currentValue = currentWeight.lbs || currentWeight.kg || '0';
      const idealValue = idealWeight.value || idealWeight.kg || '0';

      const current = Number(currentValue) || 0;
      const ideal = Number(idealValue) || 0;

      const difference = ideal - current;
      const absDifference = Math.abs(difference);
      const roundedDifference = Math.round(absDifference);

      const unit = currentWeight.type === 'lbs' ? 'lbs' : 'kg';

      if (difference > 1) {
        return t('onboarding.mealPlanContainer.gainWeight', {
          weight: roundedDifference,
          unit: unit,
        });
      } else if (difference < -1) {
        return t('onboarding.mealPlanContainer.loseWeight', {
          weight: roundedDifference,
          unit: unit,
        });
      } else {
        return t('onboarding.mealPlanContainer.maintainWeight');
      }
    } catch (error) {
      return t('onboarding.mealPlanContainer.maintainWeight');
    }
  };

  const getMealData = (mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack', dishName: string) => {
    const mapping = mealMappings[mealType][dishName];
    if (mapping) {
      return {
        dishName: t(mapping.label),
        image: mapping.image,
        mealType: t(`onboarding.mealPlanContainer.${mealType}Meal.${mealType}`),
      };
    }

    return getDay1DefaultMealData(mealType);
  };

  const getDay1DefaultMealData = (mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack') => {
    const defaults = {
      breakfast: {
        dishName: t('onboarding.mealPlanContainer.yogurtMeal.title'),
        image: yogurt,
        mealType: t('onboarding.mealPlanContainer.yogurtMeal.breakfast'),
      },
      lunch: {
        dishName: t('onboarding.mealPlanContainer.beefMeal.title'),
        image: beef,
        mealType: t('onboarding.mealPlanContainer.beefMeal.lunch'),
      },
      dinner: {
        dishName: t('onboarding.mealPlanContainer.tortillaMeal.title'),
        image: tortilla,
        mealType: t('onboarding.mealPlanContainer.tortillaMeal.dinner'),
      },
      snack: {
        dishName: t('onboarding.mealPlanContainer.toastMeal.title'),
        image: toast,
        mealType: t('onboarding.mealPlanContainer.toastMeal.snack'),
      },
    };

    return defaults[mealType];
  };

  const getDay2DefaultMealData = (mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack') => {
    const defaults = {
      breakfast: {
        dishName: t('onboarding.mealPlanContainer.yogurtMeal.title'), // старое название
        image: pancakes,
        mealType: t('onboarding.mealPlanContainer.yogurtMeal.breakfast'),
      },
      lunch: {
        dishName: t('onboarding.mealPlanContainer.beefMeal.title'), // старое название
        image: tako,
        mealType: t('onboarding.mealPlanContainer.beefMeal.lunch'),
      },
      dinner: {
        dishName: t('onboarding.mealPlanContainer.tortillaMeal.title'), // старое название
        image: dinner64,
        mealType: t('onboarding.mealPlanContainer.tortillaMeal.dinner'),
      },
      snack: {
        dishName: t('onboarding.mealPlanContainer.toastMeal.title'), // старое название
        image: humus,
        mealType: t('onboarding.mealPlanContainer.toastMeal.snack'),
      },
    };

    return defaults[mealType];
  };

  const getFirstOrDefaultMeal = (
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack',
    choices: string[]
  ) => {
    if (choices && choices.length > 0) {
      return getMealData(mealType, choices[0]);
    }
    return getDay1DefaultMealData(mealType);
  };

  const getDay1Meals = () => {
    const meals = [];

    if (breakfast === 'yes') {
      meals.push(getFirstOrDefaultMeal('breakfast', chooseBreakfast));
    }

    meals.push(getFirstOrDefaultMeal('lunch', chooseLunch));

    if (snacks === 'yes') {
      meals.push(getFirstOrDefaultMeal('snack', chooseSnack));
    }

    meals.push(getFirstOrDefaultMeal('dinner', chooseDinner));

    return meals;
  };

  const getDay2Meals = () => {
    const meals = [];

    if (breakfast === 'yes') {
      meals.push(getDay2DefaultMealData('breakfast'));
    }

    meals.push(getDay2DefaultMealData('lunch'));

    if (snacks === 'yes') {
      meals.push(getDay2DefaultMealData('snack'));
    }

    meals.push(getDay2DefaultMealData('dinner'));

    return meals;
  };

  const weightText = getWeightDifferenceText(weight, idealWeight);

  // Тайтл блока meal-plan управляется параметром personalPlanPage.mealPlanTitle:
  //   "" (пусто) или поле отсутствует → динамический тайтл по весу (как сейчас)
  //   "none"                          → тайтл не показывается
  //   любой другой текст              → свой тайтл из конфига (на текущей локали)
  const mealPlanTitleConfig = personalPlanPage?.mealPlanTitle;
  const configTitle = (mealPlanTitleConfig?.[locale] ?? mealPlanTitleConfig?.en ?? '').trim();
  const isDynamicTitle = configTitle === '';
  const titleText = isDynamicTitle ? weightText : configTitle;
  const showTitle = configTitle !== 'none';

  const day1Meals = getDay1Meals();
  const day2Meals = getDay2Meals();

  return (
    <div className={styles.mealPlanContainer}>
      {showTitle && (
        <div className={styles.mealPlanHeader}>
          <Title tag='h2' className={styles.eatTitle}>
            {titleText}
          </Title>
        </div>
      )}

      <div className={styles.mealPlan}>
        <div className={styles.mealPlanDay}>
          <Text size='m' bold className={styles.day}>
            {t('onboarding.mealPlanContainer.day1')}
          </Text>
        </div>

        {day1Meals.map((item, idx) => (
          <div className={`${styles.card} ${addBlurDay1 ? styles.blurredCard : ''}`} key={idx}>
            <div className={styles.cardImage}>
              <img className={styles.image} src={item.image} alt={item.dishName} />
            </div>

            <Text size='m' bold className={styles.cardHeader}>
              {item.dishName}
            </Text>
            <div className={styles.mealDetails}>
              <Text size='s' semibold className={styles.mealType}>
                {item.mealType}
              </Text>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.mealPlan}>
        <div className={styles.mealPlanDay}>
          <Text size='m' bold className={styles.day}>
            {t('onboarding.mealPlanContainer.day2')}
          </Text>
        </div>

        {day2Meals.map((item, idx) => (
          <div className={`${styles.card} ${styles.blurredCard}`} key={idx}>
            <div className={styles.cardImage}>
              <img className={styles.image} src={item.image} alt={item.dishName} />
            </div>

            <Text size='m' bold className={styles.cardHeader}>
              {item.dishName}
            </Text>
            <div className={styles.mealDetails}>
              <Text size='s' semibold className={styles.mealType}>
                {item.mealType}
              </Text>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MealPlanContainer;
